import { Component, OnInit, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranscriptSegment } from '../../services/transcript.service';
import { LrclibService } from '../../services/lrclib.service';
import { LyricsService } from '../../services/lyrics.service';
import { SpotifyAuthService } from '../../services/spotify-auth.service';
import { AuthService } from '../../services/auth.service';

interface SpotifyArtist { name: string; }
interface SpotifyImage { url: string; width: number; height: number; }
interface SpotifyAlbum { name: string; images: SpotifyImage[]; }

interface SpotifyTrackItem {
  id: string;
  name: string;
  artists: SpotifyArtist[];
  album: SpotifyAlbum;
  duration_ms: number;
}

interface SpotifyPlaybackState {
  is_playing: boolean;
  progress_ms: number;
  item: SpotifyTrackItem | null;
}

@Component({
  selector: 'app-sync-learning',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule],
  templateUrl: './sync-learning.page.html',
  styleUrls: ['./sync-learning.page.scss'],
})
export class SyncLearningPage implements OnInit, OnDestroy {
  isSpotifyConnected = false;
  currentTrack: SpotifyTrackItem | null = null;
  isPlaying = false;
  progressMs = 0;
  nothingPlaying = false;

  segments: TranscriptSegment[] = [];
  lyricsLoading = false;
  lyricsError = '';
  activeIndex = -1;

  translations: string[] = [];
  translationsVisible = false;
  translationsLoading = false;
  translationsLoaded = false;

  private pollInterval: any = null;
  private polling = false; // prevent concurrent polls
  private lastTrackId = '';
  private currentUser: any = null;
  private userSub!: Subscription;

  // localStorage translation cache — keyed by spotifyTrackId_toLang
  private readonly LS_TRANS_PREFIX = 'sl_trans_';

  constructor(
    private http: HttpClient,
    private spotifyAuth: SpotifyAuthService,
    private lrclibService: LrclibService,
    private lyricsService: LyricsService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.userSub = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
    this.isSpotifyConnected = this.spotifyAuth.isAuthenticated();
    if (this.isSpotifyConnected) {
      this.startPolling();
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.userSub?.unsubscribe();
  }

  connectSpotify() {
    this.spotifyAuth.login();
  }

  /** Force re-auth to pick up the new user-read-playback-state scope if needed */
  reconnectSpotify() {
    this.stopPolling();
    this.spotifyAuth.logout();
    this.spotifyAuth.login();
  }

  private startPolling() {
    this.poll();
    this.pollInterval = setInterval(() => this.poll(), 1000);
  }

  private stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private async poll() {
    if (this.polling) return;
    this.polling = true;
    try {
      const token = await this.spotifyAuth.getAccessToken();
      if (!token) {
        this.isSpotifyConnected = false;
        this.stopPolling();
        return;
      }

      // 204 = nothing active on any device → null body
      const state = await firstValueFrom(
        this.http.get<SpotifyPlaybackState | null>('https://api.spotify.com/v1/me/player').pipe(
          catchError(err => {
            if (err.status === 401) {
              this.isSpotifyConnected = false;
              this.stopPolling();
            }
            return of(null);
          })
        )
      );

      if (!state || !state.item) {
        this.nothingPlaying = true;
        this.isPlaying = false;
        return;
      }

      this.nothingPlaying = false;
      this.isPlaying = state.is_playing;
      this.progressMs = state.progress_ms;

      if (state.item.id !== this.lastTrackId) {
        this.lastTrackId = state.item.id;
        this.currentTrack = state.item;
        this.activeIndex = -1;
        await this.loadLyrics(state.item);
      }

      this.updateActiveIndex();
    } catch {
      // network error — ignore, try again next tick
    } finally {
      this.polling = false;
    }
  }

  private async loadLyrics(track: SpotifyTrackItem) {
    this.segments = [];
    this.translations = [];
    this.translationsLoaded = false;
    this.translationsVisible = false;
    this.lyricsError = '';
    this.lyricsLoading = true;

    try {
      const artistName = track.artists.map(a => a.name).join(', ');
      const segs = await this.lrclibService.getSegments(
        track.name,
        artistName,
        track.duration_ms / 1000
      );
      if (segs?.length) {
        this.segments = segs;
        // Pre-load translation cache so the toggle is instant if already cached
        const toLang = this.currentUser?.nativeLanguage ?? 'en';
        const cached = this.getTranslationCache(track.id, toLang);
        if (cached && cached.length === segs.length) {
          this.translations = cached;
          this.translationsLoaded = true;
        }
      } else {
        this.lyricsError = 'No synced lyrics found for this track on lrclib.';
      }
    } finally {
      this.lyricsLoading = false;
    }
  }

  private updateActiveIndex() {
    if (!this.segments.length) return;
    const currentSec = this.progressMs / 1000;
    let idx = -1;
    for (let i = 0; i < this.segments.length; i++) {
      if (this.segments[i].start <= currentSec) idx = i;
      else break;
    }
    if (idx !== this.activeIndex) {
      this.activeIndex = idx;
      setTimeout(() => {
        document.querySelector('.lyric-line.active')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }

  async toggleTranslations() {
    this.translationsVisible = !this.translationsVisible;
    if (this.translationsVisible && !this.translationsLoaded) {
      await this.loadTranslations();
    }
  }

  private async loadTranslations() {
    if (!this.segments.length || !this.lastTrackId) return;
    this.translationsLoading = true;
    try {
      const toLang = this.currentUser?.nativeLanguage ?? 'en';

      // Check localStorage cache first — avoids re-calling Gemini for the same track
      const cached = this.getTranslationCache(this.lastTrackId, toLang);
      if (cached && cached.length === this.segments.length) {
        this.translations = cached;
        this.translationsLoaded = true;
        return;
      }

      const lines = this.segments.map(s => s.text);
      this.translations = await this.lyricsService.translateLines(lines, 'auto', toLang);
      this.translationsLoaded = true;

      // Persist to localStorage so subsequent plays don't re-call Gemini
      this.setTranslationCache(this.lastTrackId, toLang, this.translations);
    } catch {
      this.translationsVisible = false;
    } finally {
      this.translationsLoading = false;
    }
  }

  /**
   * Seeks the active Spotify playback to the start of the tapped lyric line.
   * Requires Spotify Premium + user-modify-playback-state scope.
   * Optimistically updates the local progress so the highlight moves immediately.
   */
  async seekTo(seg: TranscriptSegment) {
    const positionMs = Math.round(seg.start * 1000);
    // Optimistic update — highlight moves immediately, next poll confirms
    this.progressMs = positionMs;
    this.updateActiveIndex();

    try {
      await firstValueFrom(
        this.http.put(
          `https://api.spotify.com/v1/me/player/seek?position_ms=${positionMs}`,
          null,
          { observe: 'response', responseType: 'text' as 'json' }
        ).pipe(catchError(() => of(null)))
      );
    } catch { /* ignore — optimistic update already applied */ }
  }

  private getTranslationCache(trackId: string, toLang: string): string[] | null {
    try {
      const raw = localStorage.getItem(`${this.LS_TRANS_PREFIX}${trackId}_${toLang}`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  private setTranslationCache(trackId: string, toLang: string, translations: string[]): void {
    try {
      localStorage.setItem(
        `${this.LS_TRANS_PREFIX}${trackId}_${toLang}`,
        JSON.stringify(translations)
      );
    } catch { /* storage full — silently skip */ }
  }

  albumArtUrl(track: SpotifyTrackItem): string {
    const imgs = track.album.images;
    return (imgs[1] ?? imgs[0])?.url ?? '';
  }

  artistNames(track: SpotifyTrackItem): string {
    return track.artists.map(a => a.name).join(', ');
  }

  formatTime(ms: number): string {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  get progressPercent(): number {
    if (!this.currentTrack?.duration_ms) return 0;
    return Math.min(100, (this.progressMs / this.currentTrack.duration_ms) * 100);
  }
}
